import type { ApiError as ApiErrorPayload, ApiMeta } from '../envelope';

/**
 * Thrown by the typed client wrapper on any non-2xx response.
 *
 * Per RFC-001 §4.3 / §6: the FE never invents error categories; it relays the
 * BE error envelope verbatim. Consumers branch on `code` (an extensible string
 * union — see `@everline/api-{public,admin,vendor}/errors`).
 */
export class ApiClientError<TCode extends string = string> extends Error {
  readonly code: TCode;
  readonly status: number;
  readonly traceId: string | undefined;
  readonly details: Record<string, unknown> | null;

  constructor(
    payload: ApiErrorPayload<TCode>,
    opts: { status: number; meta?: ApiMeta },
  ) {
    super(payload.message);
    this.name = 'ApiClientError';
    this.code = payload.code;
    this.status = opts.status;
    this.traceId = opts.meta?.traceId;
    this.details = payload.details ?? null;
  }

  /**
   * Narrow by error code without losing the BE/unknown-code split.
   * Use with `ErrorCode | (string & {})` generated unions.
   */
  is<C extends TCode>(code: C): this is ApiClientError<C> {
    return (this.code as string) === code;
  }
}

/**
 * Network / transport / non-envelope failures.
 * Distinguished from ApiClientError so callers can retry vs surface.
 */
export class TransportError extends Error {
  override readonly cause: unknown;

  constructor(message: string, cause: unknown) {
    super(message);
    this.name = 'TransportError';
    this.cause = cause;
  }
}
